/** The calendar editor's date chip and time dropdown (Google Calendar's), shared with Schedule a huddle. */
import { Button, DatePicker, Popover, Select, Stack, useFormat, useLocale } from "@bundle/ui";
import { useState } from "react";

const pad = (n: number) => String(n).padStart(2, "0");

/** A date button that opens the month picker (Google Calendar's date chip). */
export function DateField({ value, onChange, testID }: { value: string; onChange: (date: string) => void; testID: string }) {
  const fmt = useFormat();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [y, m, day] = value.split("-").map(Number);
  const label = fmt.date({
    year: y ?? 2026,
    month: m ?? 1,
    day: day ?? 1,
  } as Parameters<typeof fmt.date>[0]);
  return (
    <Popover open={open} onClose={() => setOpen(false)} anchor={<Button variant="secondary" icon="calendar" label={label} onPress={() => setOpen(!open)} testID={testID} />} width="picker">
      <DatePicker
        value={value}
        locale={locale === "pt-BR" ? "pt-BR" : "en"}
        onChange={(date) => {
          onChange(date);
          setOpen(false);
        }}
        testID={`${testID}-picker`}
      />
    </Popover>
  );
}

/** Times every 15 minutes, like Google Calendar's time dropdown. */
export function TimeSelect({ value, onChange, label, testID }: { value: string; onChange: (time: string) => void; label: string; testID: string }) {
  const fmt = useFormat();
  const options = Array.from({ length: 96 }, (_, i) => {
    const v = `${pad(Math.floor(i / 4))}:${pad((i % 4) * 15)}`;
    return {
      value: v,
      label: fmt.time(new Date(2026, 0, 1, Math.floor(i / 4), (i % 4) * 15).getTime()),
    };
  });
  if (!options.some((o) => o.value === value)) {
    const [h, mi] = value.split(":").map(Number);
    options.push({
      value,
      label: fmt.time(new Date(2026, 0, 1, h ?? 0, mi ?? 0).getTime()),
    });
    options.sort((a, b) => (a.value < b.value ? -1 : 1));
  }
  return (
    <Stack>
      <Select<string> accessibilityLabel={label} value={value} onChange={onChange} options={options} testID={testID} />
    </Stack>
  );
}
